import {Component, Input} from '@angular/core';
import {MappingFacetRow} from '../mapping-facets';

/**
 * The facet grid drawn on every Book and Draft card in the mapping review.
 *
 * ## Why a component rather than markup in the parent
 *
 * Three surfaces draw this grid - included members, excluded members and near
 * misses - and the entire value of it comes from all three being IDENTICAL.
 * The reader is meant to compare a row across cards by its position alone, so
 * a facet label that reads "Interior PDF" on one card and "Body PDF" on
 * another, or a row that appears in a different order, destroys the only thing
 * the grid is for. One component makes that drift impossible rather than
 * merely discouraged.
 *
 * ## Empty rows are drawn, never skipped
 *
 * A facet with no value still occupies its line. Skipping it would shift every
 * row below it and break alignment with the neighbouring card - which is the
 * one failure this layout cannot absorb. An unknown value is shown as a dash,
 * which reads as "nothing here" rather than as a missing row.
 *
 * ## The colour is the message
 *
 * `strength` is set from the member's `joinReason` by `mapping-facets.ts` and
 * nothing else. It is an ordinal label, never a score: the contract forbids a
 * confidence number reaching the screen, so there is no percentage here and
 * none should be added.
 */
@Component({
  selector: 'app-mapping-facet-grid',
  templateUrl: './mapping-facet-grid.component.html',
  styleUrls: ['./mapping-facet-grid.component.css']
})
export class MappingFacetGridComponent {

  @Input() rows: MappingFacetRow[] = [];

  /**
   * Compact mode drops the label column and keeps the colours.
   *
   * Used on near-miss cards, which sit in a secondary band where a full label
   * column would out-weigh the members they were rejected in favour of.
   */
  @Input() compact = false;

  /**
   * This item's id order and edit order disagree.
   *
   * Drawn on the `UPDATED` row because that is where the evidence is. See
   * `markSequenceAnomalies`.
   */
  @Input() sequenceAnomaly = false;
}
